import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import {
  Banner,
  Button,
  EmptyState,
  ErrorState,
  SelectField,
  SkeletonList,
  Spinner,
  StatusPill,
  TextField,
  focusFirstInvalid,
} from "@ui/react";
import { getBankDetails, listBanks, submitBankDetails } from "../../lib/api/contractor";
import { parseBankVerificationStatus } from "../../lib/types/enums";
import type { Bank, BankDetails } from "../../lib/types/models";
import { useAuth } from "../../lib/auth";
import { OnboardingLayout } from "./OnboardingLayout";
import { errorMessage } from "../../lib/errorMessage";

type AccountType = "personal" | "business";

/** Paystack accepts a different identity document per account type. */
const DOCUMENT_TYPES: Record<AccountType, string[]> = {
  personal: ["identityNumber", "passportNumber"],
  business: ["businessRegistrationNumber"],
};

const DOCUMENT_LABELS: Record<string, string> = {
  identityNumber: "ID number",
  passportNumber: "Passport number",
  businessRegistrationNumber: "Business registration number",
};

/** `supportedTypes` is optional on the wire; absent means "both". */
function supports(bank: Bank, accountType: AccountType): boolean {
  const types = bank.supportedTypes;
  return !types || types.length === 0 || types.includes(accountType);
}

interface FieldErrors {
  bankCode?: string;
  accountNumber?: string;
  accountName?: string;
  documentNumber?: string;
}

export function BankDetailsScreen() {
  const { refresh, signOut } = useAuth();

  const [state, setState] = useState<{
    loading: boolean;
    error: unknown;
    data: { banks: Bank[]; details: BankDetails | null } | null;
  }>({ loading: true, error: null, data: null });

  const [accountType, setAccountType] = useState<AccountType>("personal");
  const [documentType, setDocumentType] = useState<string>("identityNumber");
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const formRef = useRef<HTMLFormElement>(null);

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      // The saved record is informative, not required: a contractor who has
      // never submitted anything still needs the form, so its failure must not
      // take the bank list down with it.
      const [banks, details] = await Promise.all([
        listBanks(),
        getBankDetails().catch(() => null),
      ]);
      setState({ loading: false, error: null, data: { banks, details } });
    } catch (error) {
      setState({ loading: false, error, data: null });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const banks = state.data?.banks ?? [];
  const details = state.data?.details ?? null;

  const eligibleBanks = useMemo(
    () => banks.filter((bank) => supports(bank, accountType)),
    [banks, accountType],
  );

  const changeAccountType = (next: AccountType) => {
    setAccountType(next);

    const allowed = DOCUMENT_TYPES[next];
    if (!allowed.includes(documentType)) setDocumentType(allowed[0] ?? "identityNumber");

    // A bank that only handles personal accounts vanishes from the list when
    // "business" is chosen; leaving its code selected would submit a value the
    // contractor can no longer see.
    const selected = banks.find((bank) => bank.code === bankCode);
    if (selected && !supports(selected, next)) setBankCode("");
  };

  const documentLabel = DOCUMENT_LABELS[documentType] ?? "Document number";

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const next: FieldErrors = {};
    if (!bankCode) next.bankCode = "Choose your bank.";
    if (!accountNumber.trim()) next.accountNumber = "Enter your account number.";
    if (!accountName.trim()) next.accountName = "Enter the name on the account.";
    if (!documentNumber.trim()) next.documentNumber = `Enter your ${documentLabel.toLowerCase()}.`;
    setFieldErrors(next);
    if (Object.keys(next).length) {
      focusFirstInvalid(formRef.current);
      return;
    }

    const bank = banks.find((candidate) => candidate.code === bankCode);
    if (!bank) {
      setFieldErrors({ bankCode: "Choose your bank." });
      focusFirstInvalid(formRef.current);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      /* Answers 200, not 201, and makes two sequential Paystack calls (resolve
       * the account, then create the subaccount) behind a Cloud Run instance
       * that may be cold — a slow success is normal here, which is why the copy
       * below explains the wait instead of showing a bare spinner. */
      const result = await submitBankDetails({
        bankCode: bank.code,
        bankName: bank.name,
        accountNumber: accountNumber.trim(),
        accountName: accountName.trim(),
        accountType,
        documentType,
        documentNumber: documentNumber.trim(),
      });

      setState((current) =>
        current.data ? { ...current, data: { ...current.data, details: result } } : current,
      );

      if (result.hasPayoutAccount) {
        await refresh();
        return;
      }

      setSubmitError(
        "Your details are with your bank but the payout account isn't active yet. Give it a minute and try again.",
      );
    } catch (error) {
      setSubmitError(errorMessage(error));

      /* The handler persists `bankVerificationStatus: failed` BEFORE it returns
       * 422, and the subaccount may exist even when the response never arrived.
       * Local form state is therefore not evidence of anything — re-read the
       * server's version of the truth before deciding what to show. */
      setRecovering(true);
      try {
        const fresh = await getBankDetails();
        setState((current) =>
          current.data ? { ...current, data: { ...current.data, details: fresh } } : current,
        );
        if (fresh.hasPayoutAccount) {
          await refresh();
          return;
        }
      } catch {
        // Keep the original submission error; a failed re-read adds nothing.
      } finally {
        setRecovering(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const status = parseBankVerificationStatus(details?.bankVerificationStatus);
  const busy = submitting || recovering;

  return (
    <OnboardingLayout
      eyebrow="Payout details"
      title="Where should we pay you?"
      lead="We verify the account with Paystack and set up your payouts. Money lands here automatically once a job is signed off."
      step={4}
      footer={
        <Button variant="tertiary" onClick={() => void signOut()} disabled={busy}>
          Sign out and use a different account
        </Button>
      }
    >
      {state.loading ? (
        <div role="status">
          <span className="sr-only">Loading the list of banks</span>
          <SkeletonList rows={4} height="3.5rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={() => void load()} />
      ) : !banks.length ? (
        <EmptyState
          title="No banks available"
          action={{ label: "Try again", onClick: () => void load() }}
        >
          We couldn't get the list of South African banks from Paystack. Try again in a moment —
          nothing you entered has been lost.
        </EmptyState>
      ) : (
        <form ref={formRef} className="form" onSubmit={onSubmit} noValidate>
          {status === "pending" || status === "failed" ? (
            <div className="cluster">
              <StatusPill kind="bank" status={status} />
              <span className="muted">
                {status === "failed"
                  ? "Your bank couldn't confirm the last details we sent. Check them and submit again."
                  : "We're still waiting on your bank."}
              </span>
            </div>
          ) : null}

          {details?.accountNumberMasked ? (
            <Banner tone="info" title="On file">
              {details.bankName ? `${details.bankName} · ` : ""}
              {details.accountNumberMasked}
              {details.accountName ? ` · ${details.accountName}` : ""}
            </Banner>
          ) : null}

          {submitError ? (
            <Banner tone="danger" title="We couldn't verify that account">
              {submitError}
            </Banner>
          ) : null}

          <SelectField
            label="Account type"
            value={accountType}
            onChange={(event) => changeAccountType(event.target.value as AccountType)}
            hint="A business account needs the registered company details."
            required
          >
            <option value="personal">Personal</option>
            <option value="business">Business</option>
          </SelectField>

          <SelectField
            label="Bank"
            value={bankCode}
            onChange={(event) => setBankCode(event.target.value)}
            error={fieldErrors.bankCode ?? null}
            required
          >
            <option value="">Choose your bank</option>
            {eligibleBanks.map((bank) => (
              <option key={bank.code} value={bank.code}>
                {bank.name}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Account number"
            inputMode="numeric"
            autoComplete="off"
            value={accountNumber}
            // Paystack rejects anything but digits, and a space pasted from a
            // banking app is the most common way that happens.
            onChange={(event) => setAccountNumber(event.target.value.replace(/\D/g, ""))}
            error={fieldErrors.accountNumber ?? null}
            required
          />

          <TextField
            label="Account holder name"
            autoComplete="name"
            hint="Exactly as your bank has it — your bank checks this against the account."
            value={accountName}
            onChange={(event) => setAccountName(event.target.value)}
            error={fieldErrors.accountName ?? null}
            required
          />

          <SelectField
            label="Document type"
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value)}
            required
          >
            {(DOCUMENT_TYPES[accountType] ?? []).map((type) => (
              <option key={type} value={type}>
                {DOCUMENT_LABELS[type] ?? type}
              </option>
            ))}
          </SelectField>

          <TextField
            label={documentLabel}
            autoComplete="off"
            inputMode={documentType === "passportNumber" ? "text" : "numeric"}
            value={documentNumber}
            onChange={(event) => setDocumentNumber(event.target.value)}
            error={fieldErrors.documentNumber ?? null}
            required
          />

          {busy ? (
            <div className="cluster" role="status">
              <Spinner />
              <span className="muted">
                {recovering
                  ? "Checking what your bank saved…"
                  : "Verifying your account with your bank… this can take up to a minute."}
              </span>
            </div>
          ) : null}

          <Button type="submit" size="lg" loading={busy}>
            Verify and finish
          </Button>
        </form>
      )}
    </OnboardingLayout>
  );
}
